import { DestroyRef, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { camelCase } from 'change-case';
import { distinctUntilChanged } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { type CanvasElement, type CanvasElementId } from '@blaze-runtime/engine-domain';
import { type RuntimeMode } from '@blaze-shared/app-state-data-access';
import {
  type Connection,
  MAIN_INPUT_NAME,
  type RuntimeElement,
  type Workflow,
} from '@blaze-shared/workflow-domain';

// TODO: test
export function getWorkflowRuntimeElement(id: string): RuntimeElement {
  return { id, layerType: 'Workflow' };
}

// TODO: test
export function getUiRuntimeElement(id: CanvasElementId): RuntimeElement {
  return { id, layerType: 'Ui' };
}

export const getWorkflowInputName = (
  element: CanvasElement,
  connection?: Connection,
  wf?: Workflow,
): string => {
  let inputName = element.name;
  if (element.name.endsWith('_1')) {
    inputName = element.name.slice(0, -2);
  }

  if (element.type === 'table') {
    inputName = 'selectedRow';
  }
  if (element.type === 'list') {
    inputName = 'selectedItem';
  }
  if (element.type === 'ganttChart') {
    inputName = 'selectedTask';
  }
  if (element.type === 'chat') {
    inputName = 'selectedConversation';
  }
  if (element.type === 'kanbanBoard') {
    inputName = 'selectedCard';
  }
  inputName = camelCase(inputName);

  // Workflow might have overwritten default input name. If so, return the custom input name instead.
  if (!connection || !wf) {
    return inputName;
  }
  for (const wfInputName in wf.inputs) {
    if (wfInputName === MAIN_INPUT_NAME) {
      // Do not override input name determined by logic above with the default "input1" name.
      continue;
    }
    const connectionId = wf.inputs[wfInputName];
    if (connectionId === connection.id) {
      inputName = wfInputName;
    }
  }

  return inputName;
};

/**
 * Adds an event listener to an HTML Element which listens only in a specific RuntimeMode
 * Has to be run in injection context
 * @param type
 * @param listener
 * @param element
 * @param runtimeMode
 * @param options
 */
export function addEventListenerForRuntimeMode<K extends keyof HTMLElementEventMap>(
  type: K,
  listener: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any,
  element: HTMLElement,
  runtimeMode: RuntimeMode,
  options?: boolean | AddEventListenerOptions,
): void {
  const destroyRef = inject(DestroyRef);
  const store = inject(Store);

  const sub = store
    .select(runtimeSelectors.selectRuntimeMode)
    .pipe(distinctUntilChanged())
    .subscribe((mode) => {
      if (mode === runtimeMode) {
        element.addEventListener(type, listener, options);
      } else {
        element.removeEventListener(type, listener);
      }
    });

  destroyRef.onDestroy(() => {
    sub.unsubscribe();
  });
}
